/**
 * RapportAnnuelPage.jsx
 *
 * Rapport annuel : synthèse par contrat sur toute la période —
 * heures contractuelles vs consommées, déplacements, valorisation totale.
 * Self-contained, utilise useContratPageData via ContratPageShared.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ConsumptionDeltaCell,
  ContractAlerts,
  ContractFiltersBar,
  contratApi,
  fmtDays,
  fmtHours,
  formatCountDelta,
  formatCurrency,
  formatDaysDelta,
  formatHoursDelta,
  getCustomFieldValue,
  getFacturableMode,
  resolveEntryMatch,
  toDays,
  useContratPageData,
} from './ContratPageShared';

const PIE_COLORS = ['#facc15', '#0f766e', '#5b3a56', '#f97316', '#ef4444', '#7dd3fc', '#c4b5fd', '#94a3b8'];
const LEVEL_LABEL_BY_PROFILE = {
  admin: 'Admin',
  ingenieur: 'Ingénieur',
  pilotage: 'Pilotage',
  nonAffecte: 'Non affecté',
};
const normalizeActivity = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
const isTruthyCustomValue = (value) => ['1', 'true', 'oui', 'yes'].includes(normalizeActivity(value));
const getObjectIdValue = (value) => String(value?._id || value || '');
const fmtKm = (value) => `${Number(value || 0).toFixed(2)} km`;
const normalizePersonKey = (value) =>
  normalizeActivity(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
const formatPersonName = (value) =>
  String(value || 'Sans intervenant')
    .toLowerCase()
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());

export default function RapportAnnuelPage() {
  const COMMANDE_ADMIN_HOURLY_RATE = 80;
  const COMMANDE_INGE_HOURLY_RATE = 130;
  const formatPieValue = (value) => Number(value || 0).toFixed(2);
  const truncatePieLabel = (value) => {
    const label = String(value || '—');
    return label.length > 18 ? `${label.slice(0, 16)}...` : label;
  };
  const renderPieLabel = ({ name, value, percent }) =>
    `${truncatePieLabel(name)} ${formatPieValue(value)} (${(Number(percent || 0) * 100).toFixed(1)}%)`;

  const {
    availableYears,
    clientAutocompleteOptions,
    competenceCatalog,
    contractEntriesById,
    customEndDate,
    customStartDate,
    dateMode,
    error,
    filteredContracts,
    loading,
    mismatchContracts,
    rootOrganizationOptions,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
    setCustomEndDate,
    setCustomStartDate,
    setDateMode,
    setSelectedInfogeranceFilter,
    setSelectedOrganizationId,
    setSelectedRootOrganizationId,
    setSelectedYear,
    supportEntriesLoading,
    monthlyValueChartData,
    yearlyComparisonRows,
    trackerChartData,
    trackerLevelChartData,
    clientHoursChartData,
  } = useContratPageData();
  const [selectedContractIdForTicketDetail, setSelectedContractIdForTicketDetail] = useState('');
  const [selectedContractIdForUnknownDetail, setSelectedContractIdForUnknownDetail] = useState('');
  const [ikEntries, setIkEntries] = useState([]);
  const [ikEntriesLoading, setIkEntriesLoading] = useState(false);
  const [ikEntriesError, setIkEntriesError] = useState('');
  const [ikEntriesScope, setIkEntriesScope] = useState('global');

  const levelKeys = ['admin', 'ingenieur', 'pilotage', 'nonAffecte'];

  const ikPeriod = useMemo(() => {
    if (dateMode === 'custom') {
      if (!customStartDate || !customEndDate) return null;
      return {
        start: new Date(`${customStartDate}T00:00:00.000`).toISOString(),
        end: new Date(`${customEndDate}T23:59:59.999`).toISOString(),
      };
    }

    if (!selectedYear) return null;
    return {
      start: `${selectedYear}-01-01T00:00:00.000Z`,
      end: `${selectedYear}-12-31T23:59:59.999Z`,
    };
  }, [customEndDate, customStartDate, dateMode, selectedYear]);

  useEffect(() => {
    let active = true;

    const loadIkEntries = async () => {
      if (!ikPeriod) {
        setIkEntries([]);
        setIkEntriesError('');
        return;
      }

      setIkEntriesLoading(true);
      try {
        const params = { start: ikPeriod.start, end: ikPeriod.end };
        let response;
        try {
          response = await contratApi.get('/api/ik/global', { params });
          if (active) setIkEntriesScope('global');
        } catch (globalError) {
          response = await contratApi.get('/api/ik', { params });
          if (active) setIkEntriesScope('current-user');
        }
        if (active) {
          setIkEntries(Array.isArray(response?.data?.data) ? response.data.data : []);
          setIkEntriesError('');
        }
      } catch (e) {
        if (active) {
          setIkEntries([]);
          setIkEntriesError(e?.response?.data?.error || e.message || 'Erreur kilomètres');
        }
      } finally {
        if (active) {
          setIkEntriesLoading(false);
        }
      }
    };

    loadIkEntries();
    return () => {
      active = false;
    };
  }, [ikPeriod]);

  const contractDetailById = useMemo(() => {
    const byContract = new Map();
    const competence1Id = competenceCatalog?.competence1?.fieldId;
    const competence2Id = competenceCatalog?.competence2?.fieldId;
    filteredContracts.forEach((contract) => {
      const detail = {
        infogAdminHours: 0,
        infogEngineerHours: 0,
        commandeAdminHours: 0,
        commandeEngineerHours: 0,
        modeProjetAdminDays: 0,
        modeProjetEngineerDays: 0,
        modeProjetPilotageDays: 0,
        travelCount: 0,
        notInMatrixHours: 0,
        recupLivraisonHours: 0,
        unknownHours: 0,
        unknownEntries: [],
      };

      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const mode = getFacturableMode(entry);
        const profile = resolvedMatch.profile;
        const hours = Number(entry.hours || 0);
        const days = toDays(hours);
        const trackerName = String(entry?.ticket?.tracker || '').toLowerCase();
        const isCommandeTracker = trackerName.includes('commande');
        const isMatrixMatched = resolvedMatch.matrixMatched === true;
        const activityName = normalizeActivity(entry?.activity || entry?.activityName || entry?.activityType || '');
        const isRecupLivraison = activityName.includes('recup/livraison materiel')
          || (activityName.includes('recup') && activityName.includes('livraison') && activityName.includes('materiel'));
        const isInfogAdmin = mode === 'infogerance_oui' && profile === 'admin';
        const isInfogEngineer = mode === 'infogerance_oui' && profile === 'ingenieur';
        const isModeProjetAdmin = mode === 'mode_projet' && profile === 'admin';
        const isModeProjetEngineer = mode === 'mode_projet' && profile === 'ingenieur';
        const isFacturablePilotage = (mode === 'mode_projet' || mode === 'infogerance_oui') && profile === 'pilotage';
        const isCommandeAdmin = isCommandeTracker && profile === 'admin';
        const isCommandeEngineer = isCommandeTracker && profile === 'ingenieur';

        if (isInfogAdmin) detail.infogAdminHours += hours;
        if (isInfogEngineer) detail.infogEngineerHours += hours;
        if (isModeProjetAdmin) detail.modeProjetAdminDays += days;
        if (isModeProjetEngineer) detail.modeProjetEngineerDays += days;
        if (isFacturablePilotage) detail.modeProjetPilotageDays += days;
        if (isCommandeAdmin) detail.commandeAdminHours += hours;
        if (isCommandeEngineer) detail.commandeEngineerHours += hours;
        if (!isMatrixMatched) detail.notInMatrixHours += hours;
        if (isRecupLivraison) detail.recupLivraisonHours += hours;

        const isMappedToAnyCase = (
          isInfogAdmin
          || isInfogEngineer
          || isModeProjetAdmin
          || isModeProjetEngineer
          || isFacturablePilotage
          || isCommandeAdmin
          || isCommandeEngineer
        );
        if (!isMappedToAnyCase) {
          detail.unknownHours += hours;
          detail.unknownEntries.push({
            spentOn: entry?.spentOn || '',
            user: entry?.user || '—',
            ticketId: entry?.ticketId || '—',
            tracker: entry?.ticket?.tracker || 'Sans tracker',
            mode,
            profile,
            competence1: competence1 || '—',
            competence2: competence2 || '—',
            matrixResult: resolvedMatch.matrixMatched
              ? `Trouvé (${resolvedMatch.profile || '—'})${resolvedMatch.includedInContract ? ' - dans contrat' : ' - hors contrat'}`
              : 'Non trouvé',
            hours,
            comments: entry?.comments || '',
          });
        }

        const hasTravel = (entry.customFields || []).some(
          (field) =>
            String(field?.name || '').toLowerCase().includes('placement')
            && ['1', 'true', 'oui'].includes(String(field?.value || '').toLowerCase())
        );
        if (hasTravel) detail.travelCount += 1;
      });

      detail.commandeAdminValue = detail.commandeAdminHours * COMMANDE_ADMIN_HOURLY_RATE;
      detail.commandeEngineerValue = detail.commandeEngineerHours * COMMANDE_INGE_HOURLY_RATE;

      byContract.set(String(contract._id), detail);
    });

    return byContract;
  }, [competenceCatalog, contractEntriesById, filteredContracts]);

  // ── Table ─────────────────────────────────────────────────────────────────

  const table = useMaterialReactTable({
    columns: [
      {
        id: 'ticketDetail',
        header: 'Voir',
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedContractIdForTicketDetail(String(row.original.contractId || ''))}
          >
            Voir
          </Button>
        ),
      },
      { accessorKey: 'contractName', header: 'Contrat', size: 160 },
      { accessorKey: 'client', header: 'Client', size: 140 },
      {
        id: 'adm',
        header: 'Admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedAdmin}
            delta={row.original.deltaAdmin}
            contractValue={row.original.contractAdmin}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'ing',
        header: 'Ingénieur',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedIngenieur}
            delta={row.original.deltaIngenieur}
            contractValue={row.original.contractIngenieur}
            consumedFormatter={fmtHours}
            deltaFormatter={formatHoursDelta}
          />
        ),
      },
      {
        id: 'pil',
        header: 'Pilotage',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedPilotage}
            delta={row.original.deltaPilotage}
            contractValue={row.original.contractPilotage}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dAdm',
        header: 'Jour admin',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreAdminDays}
            delta={row.original.deltaCadreAdminDays}
            contractValue={row.original.contractCadreAdminDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dIng',
        header: 'Jour ingé',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedCadreIngenieurDays}
            delta={row.original.deltaCadreIngenieurDays}
            contractValue={row.original.contractCadreIngenieurDays}
            consumedFormatter={fmtDays}
            deltaFormatter={formatDaysDelta}
          />
        ),
      },
      {
        id: 'dep',
        header: 'Déplacements',
        Cell: ({ row }) => (
          <ConsumptionDeltaCell
            consumed={row.original.consumedTravel}
            delta={row.original.deltaTravel}
            contractValue={row.original.contractTravel}
            consumedFormatter={(v) => `${Number(v || 0)}`}
            deltaFormatter={formatCountDelta}
          />
        ),
      },
      {
        id: 'commandeAdminHours',
        header: 'Commande admin',
        Cell: ({ row }) => {
          const detail = contractDetailById.get(String(row.original.contractId));
          return fmtHours(detail?.commandeAdminHours || 0);
        },
      },
      {
        id: 'commandeEngineerHours',
        header: 'Commande ingé',
        Cell: ({ row }) => {
          const detail = contractDetailById.get(String(row.original.contractId));
          return fmtHours(detail?.commandeEngineerHours || 0);
        },
      },
      {
        id: 'unknownHours',
        header: 'Heures non trouvées',
        Cell: ({ row }) => {
          const contractId = String(row.original.contractId);
          const detail = contractDetailById.get(contractId);
          const unknownHours = Number(detail?.unknownHours || 0);
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{fmtHours(unknownHours)}</Typography>
              <Button
                size="small"
                variant="text"
                disabled={unknownHours <= 0}
                onClick={() => setSelectedContractIdForUnknownDetail(contractId)}
              >
                Voir
              </Button>
            </Stack>
          );
        },
      },
      {
        id: 'notInMatrixHours',
        header: 'Hors matrice',
        Cell: ({ row }) => {
          const detail = contractDetailById.get(String(row.original.contractId));
          return fmtHours(detail?.notInMatrixHours || 0);
        },
      },
      {
        id: 'recupLivraisonHours',
        header: 'Récup/Livraison Matériel',
        Cell: ({ row }) => {
          const detail = contractDetailById.get(String(row.original.contractId));
          return fmtHours(detail?.recupLivraisonHours || 0);
        },
      },
      {
        accessorKey: 'estimatedContractValue',
        header: 'Valeur contrat',
        Cell: ({ row }) => formatCurrency(row.original.estimatedContractValue),
      },
      {
        accessorKey: 'estimatedConsumedValue',
        header: 'Valeur consommée',
        Cell: ({ row }) => formatCurrency(row.original.estimatedConsumedValue),
      },
      {
        accessorKey: 'estimatedDeltaValue',
        header: 'Δ Valeur',
        Cell: ({ row }) => {
          const delta = row.original.estimatedDeltaValue;
          return (
            <Typography
              variant="body2"
              color={Number(delta) >= 0 ? 'success.main' : 'error.main'}
              fontWeight={600}
            >
              {formatCurrency(delta)}
            </Typography>
          );
        },
      },
    ],
    data: yearlyComparisonRows,
    state: { isLoading: loading || supportEntriesLoading },
    enablePagination: false,
    initialState: { density: 'compact' },
    enableExpanding: true,
    renderDetailPanel: ({ row }) => {
      const detail = contractDetailById.get(String(row.original.contractId));
      if (!detail) return null;

      const indicatorChip = (consumed, contractValue, unit = '', formatter = (v) => Number(v || 0).toFixed(2)) => {
        const delta = Number(contractValue || 0) - Number(consumed || 0);
        const label = `${formatter(consumed)}${unit} / ${formatter(contractValue)}${unit}`;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={delta < 0 ? 'warning' : 'success'}
              variant="outlined"
              label={label}
            />
            <Typography variant="caption" color={delta < 0 ? 'warning.main' : 'success.main'} fontWeight={700}>
              {delta < 0 ? `Dépassement: ${formatter(Math.abs(delta))}${unit}` : `Restant: ${formatter(delta)}${unit}`}
            </Typography>
          </Stack>
        );
      };

      return (
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Suivi enveloppes
              </Typography>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="caption">Heures admin infogérance</Typography>
                  {indicatorChip(row.original.consumedAdmin, row.original.contractAdmin, ' h')}
                </Box>
                <Box>
                  <Typography variant="caption">Heures ingé infogérance</Typography>
                  {indicatorChip(row.original.consumedIngenieur, row.original.contractIngenieur, ' h')}
                </Box>
                <Box>
                  <Typography variant="caption">Jours admin (mode projet)</Typography>
                  {indicatorChip(row.original.consumedCadreAdminDays, row.original.contractCadreAdminDays, ' j')}
                </Box>
                <Box>
                  <Typography variant="caption">Jours ingé (mode projet)</Typography>
                  {indicatorChip(row.original.consumedCadreIngenieurDays, row.original.contractCadreIngenieurDays, ' j')}
                </Box>
                <Box>
                  <Typography variant="caption">Jours pilotage</Typography>
                  {indicatorChip(row.original.consumedPilotage, row.original.contractPilotage, ' j')}
                </Box>
                <Box>
                  <Typography variant="caption">Déplacements</Typography>
                  {indicatorChip(
                    row.original.consumedTravel,
                    row.original.contractTravel,
                    '',
                    (v) => Number(v || 0).toFixed(2)
                  )}
                </Box>
              </Stack>
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Détail heures et valorisation
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Catégorie</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Taux</TableCell>
                      <TableCell align="right">Valeur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Infogérance - Niveau Administrateur</TableCell>
                      <TableCell align="right">{fmtHours(detail.infogAdminHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractAdminRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.infogAdminHours * Number(row.original.contractAdminRate || 0))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Infogérance - Niveau Ingénieur</TableCell>
                      <TableCell align="right">{fmtHours(detail.infogEngineerHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractIngenieurRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.infogEngineerHours * Number(row.original.contractIngenieurRate || 0))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Commandes - Niveau Administrateur</TableCell>
                      <TableCell align="right">{fmtHours(detail.commandeAdminHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(COMMANDE_ADMIN_HOURLY_RATE)}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.commandeAdminValue)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Commandes - Niveau Ingénieur</TableCell>
                      <TableCell align="right">{fmtHours(detail.commandeEngineerHours)}</TableCell>
                      <TableCell align="right">{formatCurrency(COMMANDE_INGE_HOURLY_RATE)}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.commandeEngineerValue)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Mode projet - Niveau Administrateur</TableCell>
                      <TableCell align="right">{fmtDays(detail.modeProjetAdminDays)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractCadreAdminRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.modeProjetAdminDays * Number(row.original.contractCadreAdminRate || 0))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Mode projet - Niveau Ingénieur</TableCell>
                      <TableCell align="right">{fmtDays(detail.modeProjetEngineerDays)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractCadreIngenieurRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.modeProjetEngineerDays * Number(row.original.contractCadreIngenieurRate || 0))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Pilotage</TableCell>
                      <TableCell align="right">{fmtDays(detail.modeProjetPilotageDays)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractPilotageRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(detail.modeProjetPilotageDays * Number(row.original.contractPilotageRate || 0))}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Déplacements</TableCell>
                      <TableCell align="right">{Number(detail.travelCount || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.contractTravelRate || 0))}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(row.original.consumedTravelValue || 0))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Box>
      );
    },
    renderToolbarInternalActions: ({ table: t }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={t} />
        <MRT_ToggleFiltersButton table={t} />
        <MRT_ToggleGlobalFilterButton table={t} />
        <MRT_ToggleFullScreenButton table={t} />
      </Box>
    ),
  });

  // ── Totaux pour le résumé ─────────────────────────────────────────────────

  const totals = yearlyComparisonRows.reduce(
    (acc, row) => ({
      contractValue: acc.contractValue + (row.estimatedContractValue || 0),
      consumedValue: acc.consumedValue + (row.estimatedConsumedValue || 0),
      deltaValue: acc.deltaValue + (row.estimatedDeltaValue || 0),
    }),
    { contractValue: 0, consumedValue: 0, deltaValue: 0 },
  );

  const monthlyProfitabilityData = useMemo(
    () =>
      (monthlyValueChartData || []).map((item) => {
        const contractValue =
          Number(item.adminContractValue || 0)
          + Number(item.ingenieurContractValue || 0)
          + Number(item.pilotageContractValue || 0);
        const consumedValue =
          Number(item.adminConsumedValue || 0)
          + Number(item.ingenieurConsumedValue || 0)
          + Number(item.pilotageConsumedValue || 0);
        return {
          label: item.label,
          contractValue,
          consumedValue,
          rentaValue: contractValue - consumedValue,
        };
      }),
    [monthlyValueChartData]
  );

  const yearlyProfitabilityData = useMemo(() => {
    const yearMap = new Map();
    (monthlyValueChartData || []).forEach((item) => {
      const year = String(item.monthKey || '').slice(0, 4);
      if (!year) return;
      const existing = yearMap.get(year) || {
        label: year,
        contractValue: 0,
        consumedValue: 0,
        rentaValue: 0,
      };
      existing.contractValue +=
        Number(item.adminContractValue || 0)
        + Number(item.ingenieurContractValue || 0)
        + Number(item.pilotageContractValue || 0);
      existing.consumedValue +=
        Number(item.adminConsumedValue || 0)
        + Number(item.ingenieurConsumedValue || 0)
        + Number(item.pilotageConsumedValue || 0);
      existing.rentaValue = existing.contractValue - existing.consumedValue;
      yearMap.set(year, existing);
    });
    return Array.from(yearMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [monthlyValueChartData]);

  const paretoClientData = useMemo(() => {
    const byClient = new Map();
    yearlyComparisonRows.forEach((row) => {
      const key = row.client || 'Sans client';
      const existing = byClient.get(key) || { client: key, value: 0 };
      existing.value += Number(row.estimatedConsumedValue || 0);
      byClient.set(key, existing);
    });

    const sorted = Array.from(byClient.values()).sort((a, b) => b.value - a.value);
    const total = sorted.reduce((sum, item) => sum + item.value, 0);
    let cumulative = 0;
    return sorted.map((item) => {
      cumulative += item.value;
      return {
        ...item,
        cumulativeRate: total > 0 ? (cumulative / total) * 100 : 0,
      };
    });
  }, [yearlyComparisonRows]);

  const categoryBreakdownData = useMemo(() => {
    const totals = { helpdesk: 0, commande: 0, other: 0 };
    (clientHoursChartData || []).forEach((item) => {
      totals.helpdesk += Number(item.helpdesk || 0);
      totals.commande += Number(item.commande || 0);
      totals.other += Number(item.other || 0);
    });
    return [
      { name: 'Infogérance', value: totals.helpdesk, color: '#3b82f6' },
      { name: 'Commande', value: totals.commande, color: '#f59e0b' },
      { name: 'Autres', value: totals.other, color: '#94a3b8' },
    ];
  }, [clientHoursChartData]);

  const timePieData = useMemo(() => {
    const competence1Id = competenceCatalog?.competence1?.fieldId;
    const competence2Id = competenceCatalog?.competence2?.fieldId;
    const levelMap = new Map();
    const activityMap = new Map();
    const billingMap = new Map();
    const travelerMap = new Map();
    const totals = {
      hours: 0,
      onsiteHours: 0,
      travelCount: 0,
    };

    const addHours = (map, key, hours) => {
      const label = key || '—';
      map.set(label, Number(map.get(label) || 0) + Number(hours || 0));
    };

    filteredContracts.forEach((contract) => {
      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        const hours = Number(entry.hours || 0);
        if (hours <= 0) return;
        totals.hours += hours;

        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const levelLabel = resolvedMatch.matrixMatched
          ? (LEVEL_LABEL_BY_PROFILE[resolvedMatch.profile] || resolvedMatch.profile || 'Non affecté')
          : (competence2 || '(Vide)');
        const activityLabel =
          entry?.activity
          || entry?.activityName
          || entry?.activityType
          || getCustomFieldValue(entry, ['Activité', 'Activite', 'Activity'])
          || 'Sans activité';
        const rawBillingValue = getCustomFieldValue(entry, ['Facturable ?', 'Facturable?', 'Facturable']);
        const normalizedBillingValue = normalizeActivity(rawBillingValue);
        const billingMode = getFacturableMode(entry);
        const billingLabel =
          billingMode === 'infogerance_oui'
            ? 'Infogérance'
            : billingMode === 'mode_projet'
              ? 'Mode projet'
              : normalizedBillingValue.includes('devis')
                ? 'Sur devis'
                : 'Autre / non facturable';
        const normalizedActivityLabel = normalizeActivity(activityLabel);
        const hasTravel = (entry.customFields || []).some(
          (field) =>
            normalizeActivity(field?.name).includes('placement')
            && isTruthyCustomValue(field?.value)
        );
        const isOnsite =
          hasTravel
          || normalizedActivityLabel.includes('chez client')
          || normalizedActivityLabel.includes('site client')
          || normalizedActivityLabel.includes('sur site')
          || (
            normalizedActivityLabel.includes('travail')
            && !normalizedActivityLabel.includes('distance')
          );

        addHours(levelMap, levelLabel, hours);
        addHours(activityMap, activityLabel, hours);
        addHours(billingMap, billingLabel, hours);
        if (isOnsite) totals.onsiteHours += hours;
        if (hasTravel) {
          totals.travelCount += 1;
          const userName = entry?.user || 'Sans intervenant';
          const userKey = normalizePersonKey(userName) || userName;
          const existingTraveler = travelerMap.get(userKey) || {
            user: formatPersonName(userName),
            travelCount: 0,
            travelHours: 0,
            clients: new Set(),
          };
          existingTraveler.travelCount += 1;
          existingTraveler.travelHours += hours;
          if (contract.Organization?.name) existingTraveler.clients.add(contract.Organization.name);
          travelerMap.set(userKey, existingTraveler);
        }
      });
    });

    const toRows = (map) =>
      Array.from(map.entries())
        .map(([name, value]) => ({ name, value }))
        .filter((item) => Number(item.value || 0) > 0)
        .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

    return {
      level: toRows(levelMap),
      activity: toRows(activityMap),
      billing: toRows(billingMap),
      travelers: Array.from(travelerMap.values())
        .map((item) => ({
          ...item,
          clients: Array.from(item.clients).sort((a, b) => a.localeCompare(b)).join(', '),
        }))
        .sort((a, b) => b.travelCount - a.travelCount || b.travelHours - a.travelHours),
      totals,
    };
  }, [competenceCatalog, contractEntriesById, filteredContracts]);

  const travelStats = useMemo(() => {
    const contractOrganizationIds = new Set(
      filteredContracts
        .map((contract) => getObjectIdValue(contract.Organization))
        .filter(Boolean)
    );
    const timeEntryIds = new Set();
    const ticketIds = new Set();
    const entryUserByTimeEntryId = new Map();

    filteredContracts.forEach((contract) => {
      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        const entryId = Number(entry?.entryId);
        const ticketId = Number(entry?.ticketId);
        if (Number.isFinite(entryId)) {
          timeEntryIds.add(entryId);
          entryUserByTimeEntryId.set(entryId, entry?.user || 'Sans intervenant');
        }
        if (Number.isFinite(ticketId)) ticketIds.add(ticketId);
      });
    });

    const matchedIkEntries = (ikEntries || []).filter((entry) => {
      const clientOrganizationId = getObjectIdValue(entry?.ClientOrganization || entry?.Organization);
      const timeEntryId = Number(entry?.timeEntryId);
      const ticketId = Number(entry?.ticketId);
      return (
        (clientOrganizationId && contractOrganizationIds.has(clientOrganizationId))
        || (Number.isFinite(timeEntryId) && timeEntryIds.has(timeEntryId))
        || (Number.isFinite(ticketId) && ticketIds.has(ticketId))
      );
    });

    const byUser = new Map();
    timePieData.travelers.forEach((traveler) => {
      const userKey = normalizePersonKey(traveler.user) || traveler.user;
      byUser.set(userKey, {
        user: traveler.user,
        travelCount: Number(traveler.travelCount || 0),
        travelHours: Number(traveler.travelHours || 0),
        distanceKm: 0,
        ikAmount: 0,
        reimbursedAmount: 0,
        extraExpensesAmount: 0,
        clients: traveler.clients || '',
      });
    });

    matchedIkEntries.forEach((entry) => {
      const timeEntryId = Number(entry?.timeEntryId);
      const userName =
        entry?.userName
        || entryUserByTimeEntryId.get(timeEntryId)
        || entry?.User?.email
        || 'Sans intervenant';
      const userKey = normalizePersonKey(userName) || userName;
      const existing = byUser.get(userKey) || {
        user: formatPersonName(userName),
        travelCount: 0,
        travelHours: 0,
        distanceKm: 0,
        ikAmount: 0,
        reimbursedAmount: 0,
        extraExpensesAmount: 0,
        clients: '',
      };
      existing.distanceKm += Number(entry?.distanceKm || 0);
      existing.ikAmount += Number(entry?.amount || 0);
      existing.reimbursedAmount += Number((entry?.totalReimbursedAmount ?? entry?.amount) || 0);
      existing.extraExpensesAmount += Number(entry?.extraExpensesAmount || 0);
      if (entry?.clientOrganizationName && !existing.clients.includes(entry.clientOrganizationName)) {
        existing.clients = [existing.clients, entry.clientOrganizationName].filter(Boolean).join(', ');
      }
      byUser.set(userKey, existing);
    });

    const totals = matchedIkEntries.reduce(
      (acc, entry) => {
        acc.distanceKm += Number(entry?.distanceKm || 0);
        acc.ikAmount += Number(entry?.amount || 0);
        acc.reimbursedAmount += Number((entry?.totalReimbursedAmount ?? entry?.amount) || 0);
        acc.extraExpensesAmount += Number(entry?.extraExpensesAmount || 0);
        return acc;
      },
      { distanceKm: 0, ikAmount: 0, reimbursedAmount: 0, extraExpensesAmount: 0 }
    );

    return {
      ikEntries: matchedIkEntries,
      rows: Array.from(byUser.values()).sort(
        (a, b) =>
          Number(b.travelCount || 0) - Number(a.travelCount || 0)
          || Number(b.distanceKm || 0) - Number(a.distanceKm || 0)
          || Number(b.travelHours || 0) - Number(a.travelHours || 0)
      ),
      totals,
    };
  }, [contractEntriesById, filteredContracts, ikEntries, timePieData.travelers]);

  const timeIndicatorCards = useMemo(() => {
    const topOf = (rows) => rows[0] || { name: '—', value: 0 };
    const topLevel = topOf(timePieData.level);
    const topActivity = topOf(timePieData.activity);
    const topBilling = topOf(timePieData.billing);
    const totalHours = Number(timePieData.totals?.hours || 0);
    const onsiteHours = Number(timePieData.totals?.onsiteHours || 0);
    const rate = (value) => (totalHours > 0 ? `${((Number(value || 0) / totalHours) * 100).toFixed(1)} %` : '0.0 %');

    return [
      {
        label: 'Temps total passé',
        value: fmtHours(totalHours),
        caption: `${filteredContracts.length} contrat(s) filtré(s)`,
      },
      {
        label: 'Temps chez le client',
        value: fmtHours(onsiteHours),
        caption: rate(onsiteHours),
      },
      {
        label: 'Nombre de déplacements',
        value: Number(timePieData.totals?.travelCount || 0),
        caption: ikEntriesLoading
          ? 'Chargement KM...'
          : `${travelStats.ikEntries.length} trajet(s) rattaché(s)`,
      },
      {
        label: 'KM parcourus',
        value: fmtKm(travelStats.totals.distanceKm),
        caption: 'Rattachés aux clients filtrés',
      },
      {
        label: 'Niveau dominant',
        value: topLevel.name,
        caption: `${fmtHours(topLevel.value)} · ${rate(topLevel.value)}`,
      },
      {
        label: 'Activité dominante',
        value: topActivity.name,
        caption: `${fmtHours(topActivity.value)} · ${rate(topActivity.value)}`,
      },
      {
        label: 'Facturation dominante',
        value: topBilling.name,
        caption: `${fmtHours(topBilling.value)} · ${rate(topBilling.value)}`,
      },
    ];
  }, [filteredContracts.length, ikEntriesLoading, ikEntriesScope, timePieData, travelStats]);

  const trackerLevelRows = useMemo(
    () =>
      (trackerLevelChartData || [])
        .map((item) => {
          const totalHours = levelKeys.reduce((sum, key) => sum + Number(item[key] || 0), 0);
          return { ...item, totalHours };
        })
        .sort((a, b) => Number(b.totalHours || 0) - Number(a.totalHours || 0)),
    [trackerLevelChartData]
  );

  const selectedContractForTicketDetail = useMemo(
    () => filteredContracts.find((contract) => String(contract._id) === String(selectedContractIdForTicketDetail)) || null,
    [filteredContracts, selectedContractIdForTicketDetail]
  );

  const selectedContractForUnknownDetail = useMemo(
    () => filteredContracts.find((contract) => String(contract._id) === String(selectedContractIdForUnknownDetail)) || null,
    [filteredContracts, selectedContractIdForUnknownDetail]
  );

  const selectedUnknownEntries = useMemo(
    () => (selectedContractForUnknownDetail ? (contractDetailById.get(String(selectedContractForUnknownDetail._id))?.unknownEntries || []) : []),
    [contractDetailById, selectedContractForUnknownDetail]
  );

  const selectedContractTimeDetail = useMemo(() => {
    if (!selectedContractForTicketDetail) return null;

    const competence1Id = competenceCatalog?.competence1?.fieldId;
    const competence2Id = competenceCatalog?.competence2?.fieldId;
    const entries = contractEntriesById.get(selectedContractForTicketDetail._id) || [];
    const buckets = {
      infogAdmin: [],
      infogEngineer: [],
      commandeAdmin: [],
      commandeEngineer: [],
      modeProjetAdmin: [],
      modeProjetEngineer: [],
      modeProjetPilotage: [],
    };

    entries.forEach((entry) => {
      const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
      const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
      const resolvedMatch = resolveEntryMatch(selectedContractForTicketDetail, competence1, competence2);
      const mode = getFacturableMode(entry);
      const profile = resolvedMatch.profile;
      const trackerName = String(entry?.ticket?.tracker || '').toLowerCase();
      const isCommandeTracker = trackerName.includes('commande');
      const hours = Number(entry.hours || 0);

      const row = {
        spentOn: entry?.spentOn || '',
        user: entry?.user || '—',
        ticketId: entry?.ticketId || '—',
        tracker: entry?.ticket?.tracker || 'Sans tracker',
        hours,
        days: toDays(hours),
        comments: entry?.comments || '',
      };

      if (mode === 'infogerance_oui' && profile === 'admin') buckets.infogAdmin.push(row);
      if (mode === 'infogerance_oui' && profile === 'ingenieur') buckets.infogEngineer.push(row);
      if (isCommandeTracker && profile === 'admin') buckets.commandeAdmin.push(row);
      if (isCommandeTracker && profile === 'ingenieur') buckets.commandeEngineer.push(row);
      if (mode === 'mode_projet' && profile === 'admin') buckets.modeProjetAdmin.push(row);
      if (mode === 'mode_projet' && profile === 'ingenieur') buckets.modeProjetEngineer.push(row);
      if ((mode === 'mode_projet' || mode === 'infogerance_oui') && resolvedMatch.matrixMatched && profile === 'pilotage') {
        buckets.modeProjetPilotage.push(row);
      }
    });

    return buckets;
  }, [competenceCatalog, contractEntriesById, selectedContractForTicketDetail]);

  const timeDetailSections = useMemo(() => {
    if (!selectedContractTimeDetail) return [];
    return [
      { key: 'infog-admin', title: 'Infogérance - Admin', note: 'Décompter des heures admin', unit: 'hours', rows: selectedContractTimeDetail.infogAdmin },
      { key: 'infog-inge', title: 'Infogérance - Ingé', note: 'Décompter des heures ingé', unit: 'hours', rows: selectedContractTimeDetail.infogEngineer },
      { key: 'commande-admin', title: 'Commande - Admin', note: 'Ne pas décompter du contrat', unit: 'hours', rows: selectedContractTimeDetail.commandeAdmin },
      { key: 'commande-inge', title: 'Commande - Ingé', note: 'Ne pas décompter du contrat', unit: 'hours', rows: selectedContractTimeDetail.commandeEngineer },
      { key: 'mode-projet-admin', title: 'Mode projet - Admin', note: 'Décompter des jours admin', unit: 'days', rows: selectedContractTimeDetail.modeProjetAdmin },
      { key: 'mode-projet-inge', title: 'Mode projet - Ingé', note: 'Décompter des jours ingé', unit: 'days', rows: selectedContractTimeDetail.modeProjetEngineer },
      { key: 'mode-projet-pilotage', title: 'Mode projet - Pilotage', note: 'Décompter des jours pilotage', unit: 'days', rows: selectedContractTimeDetail.modeProjetPilotage },
    ];
  }, [selectedContractTimeDetail]);

  return (
    <Box>
      {/* ── En-tête ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Rapport annuel</Typography>
          <Typography variant="caption" color="text.secondary">
            Synthèse par contrat sur la période — heures, déplacements et valorisation.
          </Typography>
        </Box>
        {supportEntriesLoading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Chargement des imputations…</Typography>
          </Stack>
        )}
      </Stack>

      {/* ── Filtres ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <ContractFiltersBar
          availableYears={availableYears}
          clientAutocompleteOptions={clientAutocompleteOptions}
          customEndDate={customEndDate}
          customStartDate={customStartDate}
          dateMode={dateMode}
          onChangeCustomEndDate={setCustomEndDate}
          onChangeCustomStartDate={setCustomStartDate}
          onChangeDateMode={setDateMode}
          onChangeInfogerance={setSelectedInfogeranceFilter}
          onChangeOrganization={setSelectedOrganizationId}
          onChangeRootOrganization={setSelectedRootOrganizationId}
          onChangeYear={setSelectedYear}
          rootOrganizationOptions={rootOrganizationOptions}
          selectedInfogeranceFilter={selectedInfogeranceFilter}
          selectedOrganizationId={selectedOrganizationId}
          selectedRootOrganizationId={selectedRootOrganizationId}
          selectedYear={selectedYear}
        />
      </Paper>

      {/* ── Alertes & erreurs ── */}
      <ContractAlerts
        competenceCatalog={competenceCatalog}
        mismatchContracts={mismatchContracts}
        supportEntriesLoading={supportEntriesLoading}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Résumé valorisation ── */}
      {yearlyComparisonRows.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          {[
            { label: 'Valeur contractuelle totale', value: totals.contractValue, color: 'text.primary' },
            { label: 'Valeur consommée totale', value: totals.consumedValue, color: 'text.primary' },
            {
              label: 'Delta (rentabilité)',
              value: totals.deltaValue,
              color: totals.deltaValue >= 0 ? 'success.main' : 'error.main',
            },
          ].map((item) => (
            <Paper key={item.label} variant="outlined" sx={{ p: 1.5, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {item.label}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color={item.color}>
                {formatCurrency(item.value)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ── Table ── */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1}>
          {filteredContracts.length} contrat(s) sur la période
        </Typography>
        <MaterialReactTable table={table} />
      </Paper>

      {/* ── Graphes ── */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Répartition du temps passé
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{
            mb: 2,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
              xl: 'repeat(7, minmax(0, 1fr))',
            },
          }}
        >
          {timeIndicatorCards.map((item) => (
            <Card key={item.label} variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.label}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    mt: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={String(item.value)}
                >
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {item.caption}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
          {[
            { title: 'Temps passé par niveau', data: timePieData.level },
            { title: 'Temps passé par activité', data: timePieData.activity },
            { title: 'Temps passé par type facturation', data: timePieData.billing },
          ].map((chart) => (
            <Box
              key={chart.title}
              sx={{
                flex: 1,
                minWidth: 0,
                border: '1px solid',
                borderColor: 'divider',
                p: 1.5,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {chart.title}
              </Typography>
              <Box sx={{ height: 310 }}>
                {chart.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chart.data}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={92}
                        labelLine
                        label={renderPieLabel}
                      >
                        {chart.data.map((item, index) => (
                          <Cell key={`${chart.title}-${item.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => `${Number(value || 0).toFixed(2)} h`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                    <Typography variant="body2" color="text.secondary">
                      Aucune donnée sur la période.
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Box>
          ))}
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Personnes qui se sont le plus déplacées
            </Typography>
            {ikEntriesLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Chargement KM</Typography>
              </Stack>
            ) : null}
          </Stack>
          {ikEntriesError ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Données kilomètres indisponibles: {ikEntriesError}
            </Alert>
          ) : null}
          {!ikEntriesError && ikEntriesScope !== 'global' ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Accès global indisponible: les kilomètres affichés sont limités à l&apos;utilisateur connecté.
            </Alert>
          ) : null}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Intervenant</TableCell>
                  <TableCell align="right">Déplacements</TableCell>
                  <TableCell align="right">Temps sur déplacement</TableCell>
                  <TableCell align="right">KM</TableCell>
                  <TableCell>Clients rattachés</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {travelStats.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun déplacement rattaché à la période filtrée.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : travelStats.rows.slice(0, 15).map((row) => (
                  <TableRow key={row.user}>
                    <TableCell>{row.user}</TableCell>
                    <TableCell align="right">{Number(row.travelCount || 0)}</TableCell>
                    <TableCell align="right">{fmtHours(row.travelHours)}</TableCell>
                    <TableCell align="right">{fmtKm(row.distanceKm)}</TableCell>
                    <TableCell>{row.clients || '—'}</TableCell>
                  </TableRow>
                ))}
                {travelStats.rows.length > 0 ? (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {Number(timePieData.totals?.travelCount || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmtHours(timePieData.travelers.reduce((sum, row) => sum + Number(row.travelHours || 0), 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtKm(travelStats.totals.distanceKm)}</TableCell>
                    <TableCell />
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Rentabilité par mois
        </Typography>
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyProfitabilityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${(Number(v || 0) / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="contractValue" name="Contractuel" fill="#90caf9" />
              <Bar dataKey="consumedValue" name="Consommé" fill="#f59e0b" />
              <Line type="monotone" dataKey="rentaValue" name="Rentabilité" stroke="#16a34a" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            Rentabilité agrégée par année
          </Typography>
          {dateMode === 'year' && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const endYear = Number(selectedYear || new Date().getFullYear());
                const startYear = endYear - 4;
                setDateMode('custom');
                setCustomStartDate(`${startYear}-01-01`);
                setCustomEndDate(`${endYear}-12-31`);
              }}
            >
              Voir évolution 5 ans
            </Button>
          )}
        </Stack>
        {dateMode === 'year' && yearlyProfitabilityData.length <= 1 ? (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            En mode “Par année”, ce graphe contient une seule année. Utilise “Voir évolution 5 ans”.
          </Typography>
        ) : null}
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={yearlyProfitabilityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${(Number(v || 0) / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="contractValue" name="Contractuel" fill="#93c5fd" />
              <Bar dataKey="consumedValue" name="Consommé" fill="#fb7185" />
              <Line type="monotone" dataKey="rentaValue" name="Rentabilité" stroke="#15803d" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Pareto clients (valeur consommée)
        </Typography>
        <Box sx={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={paretoClientData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="client" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${(Number(v || 0) / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${Number(v || 0).toFixed(0)}%`} tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v, name) => (name === 'Cumul %' ? `${Number(v || 0).toFixed(1)} %` : formatCurrency(v))} />
              <Legend />
              <Bar yAxisId="left" dataKey="value" name="Valeur consommée" fill="#60a5fa" />
              <Line yAxisId="right" type="monotone" dataKey="cumulativeRate" name="Cumul %" stroke="#dc2626" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          Détail tickets par catégorie, tracker et niveau
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Heures par tracker (Top 12)
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(trackerChartData || []).slice(0, 12)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10 }} />
                  <RechartsTooltip formatter={(v) => `${Number(v || 0).toFixed(2)} h`} />
                  <Bar dataKey="value" name="Heures">
                    {(trackerChartData || []).slice(0, 12).map((item, idx) => (
                      <Cell
                        key={`${item.name}-${idx}`}
                        fill={['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444'][idx % 5]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Répartition par catégorie d&apos;imputation
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryBreakdownData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${Number(v || 0).toFixed(0)} h`} tick={{ fontSize: 11 }} />
                  <RechartsTooltip formatter={(v) => `${Number(v || 0).toFixed(2)} h`} />
                  <Bar dataKey="value" name="Heures">
                    {categoryBreakdownData.map((item) => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Trackers par niveau
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tracker</TableCell>
                    <TableCell align="right">Admin</TableCell>
                    <TableCell align="right">Ingénieur</TableCell>
                    <TableCell align="right">Pilotage</TableCell>
                    <TableCell align="right">Non affecté</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackerLevelRows.slice(0, 15).map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>{row.name || 'Sans tracker'}</TableCell>
                      {levelKeys.map((key) => (
                        <TableCell key={`${row.name}-${key}`} align="right">
                          {Number(row[key] || 0).toFixed(2)} h
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {Number(row.totalHours || 0).toFixed(2)} h
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Clients par catégorie (Top 15)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell align="right">Infogérance</TableCell>
                    <TableCell align="right">Commande</TableCell>
                    <TableCell align="right">Autres</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(clientHoursChartData || []).slice(0, 15).map((row) => {
                    const total = Number(row.helpdesk || 0) + Number(row.commande || 0) + Number(row.other || 0);
                    return (
                      <TableRow key={row.name}>
                        <TableCell>{row.name || 'Sans client'}</TableCell>
                        <TableCell align="right">{Number(row.helpdesk || 0).toFixed(2)} h</TableCell>
                        <TableCell align="right">{Number(row.commande || 0).toFixed(2)} h</TableCell>
                        <TableCell align="right">{Number(row.other || 0).toFixed(2)} h</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{total.toFixed(2)} h</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(selectedContractForTicketDetail)}
        onClose={() => setSelectedContractIdForTicketDetail('')}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {`Temps passés - ${selectedContractForTicketDetail?.name || 'Contrat'}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {timeDetailSections.map((section) => {
              const totalHours = section.rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);
              const totalDays = section.rows.reduce((sum, row) => sum + Number(row.days || 0), 0);
              return (
                <Paper key={section.key} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={700}>{section.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{section.note}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {section.unit === 'days' ? `Total: ${fmtDays(totalDays)}` : `Total: ${fmtHours(totalHours)}`}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Intervenant</TableCell>
                          <TableCell>Ticket</TableCell>
                          <TableCell>Tracker</TableCell>
                          <TableCell align="right">{section.unit === 'days' ? 'Jours' : 'Heures'}</TableCell>
                          <TableCell>Commentaire</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="body2" color="text.secondary">Aucun temps saisi.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : section.rows.map((row, index) => (
                          <TableRow key={`${section.key}-${row.ticketId}-${index}`}>
                            <TableCell>{row.spentOn ? new Date(row.spentOn).toLocaleDateString('fr-FR') : '—'}</TableCell>
                            <TableCell>{row.user}</TableCell>
                            <TableCell>{row.ticketId}</TableCell>
                            <TableCell>{row.tracker}</TableCell>
                            <TableCell align="right">
                              {section.unit === 'days' ? Number(row.days || 0).toFixed(2) : Number(row.hours || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>{row.comments || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedContractIdForTicketDetail('')}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedContractForUnknownDetail)}
        onClose={() => setSelectedContractIdForUnknownDetail('')}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {`Heures non trouvées - ${selectedContractForUnknownDetail?.name || 'Contrat'}`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Temps qui ne rentrent dans aucune case de décompte (infogérance, commande, mode projet admin/ingé/pilotage).
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Total: {fmtHours(selectedUnknownEntries.reduce((sum, row) => sum + Number(row.hours || 0), 0))}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Intervenant</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Tracker</TableCell>
                  <TableCell>Mode facturable</TableCell>
                  <TableCell>Profil matrice</TableCell>
                  <TableCell>Compétence 1</TableCell>
                  <TableCell>Compétence 2</TableCell>
                  <TableCell>Résultat matrice</TableCell>
                  <TableCell align="right">Heures</TableCell>
                  <TableCell>Commentaire</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedUnknownEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Typography variant="body2" color="text.secondary">Aucune heure non trouvée.</Typography>
                    </TableCell>
                  </TableRow>
                ) : selectedUnknownEntries.map((row, index) => (
                  <TableRow key={`unknown-${row.ticketId}-${index}`}>
                    <TableCell>{row.spentOn ? new Date(row.spentOn).toLocaleDateString('fr-FR') : '—'}</TableCell>
                    <TableCell>{row.user}</TableCell>
                    <TableCell>{row.ticketId}</TableCell>
                    <TableCell>{row.tracker}</TableCell>
                    <TableCell>{row.mode || '—'}</TableCell>
                    <TableCell>{row.profile || '—'}</TableCell>
                    <TableCell>{row.competence1 || '—'}</TableCell>
                    <TableCell>{row.competence2 || '—'}</TableCell>
                    <TableCell>{row.matrixResult || '—'}</TableCell>
                    <TableCell align="right">{Number(row.hours || 0).toFixed(2)}</TableCell>
                    <TableCell>{row.comments || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedContractIdForUnknownDetail('')}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
